import { Controller } from "@hotwired/stimulus"

const TOTAL_STEPS = 4
const MEMBER_MAX = 100
const FIXED_SIGNUP_GOAL = 30

const GOAL_PCT = {
  ten: 0.1,
  twenty: 0.2,
  thirty_pct: 0.3
}

export default class extends Controller {
  static targets = [
    "step",
    "progressFill",
    "progressLabel",
    "backButton",
    "successPanel",
    "wizardRoot",
    "goalCard",
    "error",
    "growthGoalField",
    "hiddenLastMonth",
    "hiddenMtd",
    "lastMonthShow",
    "lastMonthDisplay",
    "lastMonthEdit",
    "lastMonthSlider",
    "mtdShow",
    "mtdDisplay",
    "mtdEdit",
    "mtdSlider",
    "vizHeadlineProjected",
    "vizSubline",
    "vizPaceBadge",
    "vizLegendLine",
    "vizMtdFill",
    "vizProjectedFill",
    "vizMarkerLast",
    "vizMarkerProjected",
    "vizMarkerGoal",
    "vizGapVal",
    "vizDateHint",
    "howToReachLine",
    "stepFourPitch",
    "fixedThirtyWrap",
    "submitGap",
    "submitTarget",
    "submitCurrentProjected",
    "submitCpl"
  ]

  static values = {
    step: { type: Number, default: 1 }
  }

  connect() {
    this.mtdUserEdited = false
    const last = this.parseCount(this.hiddenLastMonthTarget?.value) ?? 24
    this.syncLastMonth(last, false)
    this.clearGoalSelection()
    this.render()
  }

  goalTargetSignups(lastSignups, goalKey) {
    if (goalKey === "fixed30") return FIXED_SIGNUP_GOAL
    const p = GOAL_PCT[goalKey]
    if (p == null) return null
    return Math.max(0, Math.round(lastSignups * (1 + p)))
  }

  isValidGoalKey(g) {
    return g === "fixed30" || GOAL_PCT[g] != null
  }

  lastMonthSliderInput(event) {
    const v = parseInt(event.target.value, 10)
    if (!Number.isFinite(v)) return
    this.syncLastMonth(Math.min(Math.max(0, v), MEMBER_MAX), true)
  }

  mtdSliderInput(event) {
    const v = parseInt(event.target.value, 10)
    if (!Number.isFinite(v)) return
    this.mtdUserEdited = true
    this.syncMtd(Math.min(Math.max(0, v), MEMBER_MAX), true)
  }

  beginEditLastMonth() {
    if (!this.hasLastMonthEditTarget || !this.hasLastMonthShowTarget) return
    this.lastMonthShowTarget.classList.add("hidden")
    this.lastMonthEditTarget.classList.remove("hidden")
    this.lastMonthEditTarget.value = this.hiddenLastMonthTarget?.value || ""
    this.lastMonthEditTarget.focus()
    this.lastMonthEditTarget.select()
  }

  commitLastMonth() {
    if (!this.hasLastMonthEditTarget) return
    if (this.lastMonthEditTarget.classList.contains("hidden")) return
    const n = this.parseCountInput(this.lastMonthEditTarget.value)
    if (n === null || n < 0) {
      this.cancelEditLastMonth()
      return
    }
    this.syncLastMonth(Math.min(n, MEMBER_MAX), true)
    this.lastMonthEditTarget.classList.add("hidden")
    if (this.hasLastMonthShowTarget) this.lastMonthShowTarget.classList.remove("hidden")
  }

  cancelEditLastMonth() {
    if (this.hasLastMonthEditTarget) {
      this.lastMonthEditTarget.classList.add("hidden")
      this.lastMonthShowTarget?.classList.remove("hidden")
    }
  }

  lastMonthEditKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitLastMonth()
    }
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelEditLastMonth()
    }
  }

  beginEditMtd() {
    if (!this.hasMtdEditTarget || !this.hasMtdShowTarget) return
    this.mtdShowTarget.classList.add("hidden")
    this.mtdEditTarget.classList.remove("hidden")
    this.mtdEditTarget.value = this.hiddenMtdTarget?.value || ""
    this.mtdEditTarget.focus()
    this.mtdEditTarget.select()
  }

  commitMtd() {
    if (!this.hasMtdEditTarget) return
    if (this.mtdEditTarget.classList.contains("hidden")) return
    const n = this.parseCountInput(this.mtdEditTarget.value)
    if (n === null || n < 0) {
      this.cancelEditMtd()
      return
    }
    this.mtdUserEdited = true
    this.syncMtd(Math.min(n, MEMBER_MAX), true)
    this.mtdEditTarget.classList.add("hidden")
    if (this.hasMtdShowTarget) this.mtdShowTarget.classList.remove("hidden")
  }

  cancelEditMtd() {
    if (this.hasMtdEditTarget) {
      this.mtdEditTarget.classList.add("hidden")
      this.mtdShowTarget?.classList.remove("hidden")
    }
  }

  mtdEditKeydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.commitMtd()
    }
    if (event.key === "Escape") {
      event.preventDefault()
      this.cancelEditMtd()
    }
  }

  syncLastMonth(count, recalc = true) {
    if (this.hasHiddenLastMonthTarget) this.hiddenLastMonthTarget.value = String(count)
    if (this.hasLastMonthDisplayTarget) this.lastMonthDisplayTarget.textContent = this.formatCount(count)
    if (this.hasLastMonthSliderTarget) this.syncSlider(this.lastMonthSliderTarget, count)
    if (!this.mtdUserEdited) {
      this.syncMtd(this.defaultMtdFromLastSignups(count), false)
    }
    if (recalc) this.recalculateViz()
  }

  syncMtd(mtdSignups, recalc = true) {
    if (this.hasHiddenMtdTarget) this.hiddenMtdTarget.value = String(mtdSignups)
    if (this.hasMtdDisplayTarget) this.mtdDisplayTarget.textContent = this.formatCount(mtdSignups)
    if (this.hasMtdSliderTarget) this.syncSlider(this.mtdSliderTarget, mtdSignups)
    if (recalc) this.recalculateViz()
  }

  syncSlider(el, value) {
    if (!el) return
    const s = String(value)
    if (el.value !== s) el.value = s
  }

  parseCount(raw) {
    const n = parseInt(String(raw ?? ""), 10)
    return Number.isFinite(n) ? n : null
  }

  parseCountInput(raw) {
    if (raw == null || String(raw).trim() === "") return null
    const cleaned = String(raw).replace(/[^0-9]/g, "")
    if (cleaned === "") return null
    const n = parseInt(cleaned, 10)
    return Number.isFinite(n) ? n : null
  }

  pickGoal(event) {
    const goal = event.currentTarget?.dataset?.goal
    if (goal) this.applyGoalSelection(goal)
    this.clearError()
  }

  clearGoalSelection() {
    if (this.hasGrowthGoalFieldTarget) this.growthGoalFieldTarget.value = ""
    this.goalCardTargets.forEach((card) => {
      card.classList.remove(
        "ring-2",
        "ring-primary-500",
        "border-primary-500/50",
        "bg-primary-500/10",
        "scale-[1.02]",
        "shadow-lg"
      )
      card.setAttribute("aria-pressed", "false")
    })
  }

  applyGoalSelection(goal) {
    if (!this.hasGrowthGoalFieldTarget) return
    this.growthGoalFieldTarget.value = goal
    this.goalCardTargets.forEach((card) => {
      const on = card.dataset.goal === goal
      card.classList.toggle("ring-2", on)
      card.classList.toggle("ring-primary-500", on)
      card.classList.toggle("border-primary-500/50", on)
      card.classList.toggle("bg-primary-500/10", on)
      card.classList.toggle("scale-[1.02]", on)
      card.classList.toggle("shadow-lg", on)
      card.setAttribute("aria-pressed", on ? "true" : "false")
    })
    this.recalculateViz()
  }

  daysInPreviousCalendarMonth(date = new Date()) {
    const y = date.getFullYear()
    const m = date.getMonth()
    return new Date(y, m, 0).getDate()
  }

  modeledMtdFromLastSignups(lastSignups, date = new Date()) {
    const dimPrev = this.daysInPreviousCalendarMonth(date)
    const dom = Math.max(date.getDate(), 1)
    if (dimPrev <= 0 || lastSignups <= 0) return 0
    return Math.min(MEMBER_MAX, Math.round((lastSignups / dimPrev) * dom))
  }

  defaultMtdFromLastSignups(lastSignups, date = new Date()) {
    const base = this.modeledMtdFromLastSignups(lastSignups, date)
    const fromModel = Math.round(base * 0.9)
    if (fromModel > 0) return Math.min(MEMBER_MAX, fromModel)
    const y = date.getFullYear()
    const m = date.getMonth()
    const dom = Math.max(date.getDate(), 1)
    const daysInMonth = new Date(y, m + 1, 0).getDate()
    if (lastSignups <= 0 || daysInMonth <= 0) return 0
    const tenPctMtd = Math.round(lastSignups * 0.1 * (dom / daysInMonth))
    return Math.min(MEMBER_MAX, Math.max(0, tenPctMtd))
  }

  calendarContext() {
    const now = new Date()
    const y = now.getFullYear()
    const m = now.getMonth()
    const dayOfMonth = now.getDate()
    const daysInMonth = new Date(y, m + 1, 0).getDate()
    return { dayOfMonth, daysInMonth, monthName: now.toLocaleString("en-US", { month: "long" }), now }
  }

  datePlusDays(days) {
    const d = new Date()
    d.setDate(d.getDate() + days)
    return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
  }

  refreshStepFourPitch() {
    if (!this.hasStepFourPitchTarget) return
    const S_last = this.parseCount(this.hiddenLastMonthTarget?.value) ?? 0
    const goalKey = (this.growthGoalFieldTarget?.value || "").trim()
    const goal = this.goalTargetSignups(S_last, goalKey)
    if (goal == null) return
    const dateStr = this.datePlusDays(30)
    this.stepFourPitchTarget.textContent =
      `Get a free plan to hit ${this.formatCount(goal)} sign-ups by ${dateStr}`
  }

  recalculateViz() {
    const S_last = this.parseCount(this.hiddenLastMonthTarget?.value) ?? 0
    const S_mtd = this.parseCount(this.hiddenMtdTarget?.value) ?? 0
    const goalKey = (this.growthGoalFieldTarget?.value || "").trim()
    const hasGoal = this.isValidGoalKey(goalKey)

    const { dayOfMonth, daysInMonth } = this.calendarContext()
    const dom = Math.max(dayOfMonth, 1)

    const S_goal = hasGoal ? this.goalTargetSignups(S_last, goalKey) : null

    const rawProjected = Math.round((S_mtd / dom) * daysInMonth)
    const projected =
      hasGoal && S_goal != null ? Math.min(rawProjected, S_goal) : rawProjected
    const onPaceToGoalOrBeyond = hasGoal && S_goal != null && rawProjected >= S_goal

    const maxScale = Math.max(S_last, S_mtd, rawProjected, projected, S_goal ?? 0, 1)
    const pctOf = (v) => Math.min(100, (v / maxScale) * 100)

    if (this.hasVizHeadlineProjectedTarget) {
      this.vizHeadlineProjectedTarget.textContent = this.formatCount(projected)
    }
    if (this.hasVizSublineTarget) {
      this.vizSublineTarget.textContent = "Projected new sign-ups this month"
    }

    // Layered bar: projected fill behind, current on top
    if (this.hasVizProjectedFillTarget) {
      this.vizProjectedFillTarget.style.width = `${pctOf(projected)}%`
    }
    if (this.hasVizMtdFillTarget) {
      this.vizMtdFillTarget.style.width = `${pctOf(S_mtd)}%`
    }

    const setMarker = (el, v) => {
      if (!el) return
      el.style.left = `${pctOf(v)}%`
    }
    setMarker(this.hasVizMarkerLastTarget ? this.vizMarkerLastTarget : null, S_last)
    if (this.hasVizMarkerGoalTarget) {
      if (!hasGoal || S_goal == null) {
        this.vizMarkerGoalTarget.classList.add("hidden")
      } else {
        this.vizMarkerGoalTarget.classList.remove("hidden")
        setMarker(this.vizMarkerGoalTarget, S_goal)
      }
    }

    // Legend: current fill, projected fill, last month marker, goal marker
    if (this.hasVizLegendLineTarget) {
      const items = [
        `<span class="inline-flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-sm bg-gradient-to-r from-primary-600 to-primary-400"></span><strong class="font-semibold text-gray-200">${this.formatCount(S_mtd)}</strong> so far</span>`,
        `<span class="inline-flex items-center gap-1.5"><span class="h-2.5 w-2.5 rounded-sm bg-primary-500/20 ring-1 ring-primary-500/40"></span><strong class="font-semibold text-gray-200">${this.formatCount(projected)}</strong> projected</span>`,
        `<span class="inline-flex items-center gap-1.5"><span class="h-3 w-0.5 rounded-full bg-white/70"></span><strong class="font-semibold text-gray-200">${this.formatCount(S_last)}</strong> last month</span>`
      ]
      if (hasGoal && S_goal != null) {
        items.push(`<span class="inline-flex items-center gap-1.5"><span class="h-3 w-0.5 rounded-full bg-secondary-400"></span><strong class="font-semibold text-gray-200">${this.formatCount(S_goal)}</strong> goal</span>`)
      }
      this.vizLegendLineTarget.innerHTML = items.join("")
    }

    if (this.hasVizGapValTarget) {
      if (!hasGoal || S_goal == null) {
        this.vizGapValTarget.textContent = ""
      } else if (onPaceToGoalOrBeyond) {
        this.vizGapValTarget.textContent =
          `You\u2019re on pace to hit ${this.formatCount(S_goal)}. Let us help you lock it in.`
      } else {
        const gap = S_goal - projected
        if (gap <= 0) {
          this.vizGapValTarget.textContent =
            `You\u2019re trending toward ${this.formatCount(S_goal)}. Let us help you lock it in.`
        } else {
          this.vizGapValTarget.textContent =
            `You\u2019re ${this.formatCount(gap)} sign-ups short of ${this.formatCount(S_goal)}. We have a plan.`
        }
      }
    }

    this.goalCardTargets.forEach((card) => {
      const g = card.dataset.goal
      const hint = card.querySelector("[data-goal-target-hint]")
      if (!hint) return
      if (g === "fixed30") {
        hint.textContent = `Goal: ${FIXED_SIGNUP_GOAL} new members this month`
      } else if (GOAL_PCT[g] != null) {
        const t = this.goalTargetSignups(S_last, g)
        hint.textContent = `Goal: ${this.formatCount(t)} sign-ups this month`
      } else {
        hint.textContent = "\u2014"
      }
    })
  }

  formatCount(n) {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n)
  }

  nextStep(event) {
    event?.preventDefault()
    this.clearError()
    const s = this.stepValue

    if (s === 1) {
      this.commitLastMonth()
      if (!this.validateAdvance(1)) return
      this.mtdUserEdited = false
      const L = this.parseCount(this.hiddenLastMonthTarget?.value) ?? 0
      this.syncMtd(this.defaultMtdFromLastSignups(L), true)
      this.stepValue = 2
    } else if (s === 2) {
      this.commitMtd()
      if (!this.validateAdvance(2)) return
      this.stepValue = 3
    } else if (s === 3) {
      if (!this.validateAdvance(3)) return
      this.stepValue = 4
    }

    this.render()
  }

  back(event) {
    event?.preventDefault()
    this.clearError()
    if (this.stepValue > 1) {
      this.stepValue--
      this.render()
    }
  }

  submit(event) {
    this.clearError()
    if (this.stepValue !== TOTAL_STEPS) {
      event.preventDefault()
      return
    }
    if (!this.validateStep4()) {
      event.preventDefault()
      return
    }
    this.fillGrowthPlanHiddenFields()
  }

  fillGrowthPlanHiddenFields() {
    const S_last = this.parseCount(this.hiddenLastMonthTarget?.value) ?? 0
    const S_mtd = this.parseCount(this.hiddenMtdTarget?.value) ?? 0
    const goalKey = (this.growthGoalFieldTarget?.value || "").trim()
    const S_goal = this.isValidGoalKey(goalKey) ? this.goalTargetSignups(S_last, goalKey) : S_last

    const { dayOfMonth, daysInMonth } = this.calendarContext()
    const dom = Math.max(dayOfMonth, 1)
    const projected = Math.round((S_mtd / dom) * daysInMonth)
    const gap = Math.max(S_goal - projected, 1)

    if (this.hasSubmitGapTarget) this.submitGapTarget.value = String(gap)
    if (this.hasSubmitTargetTarget) this.submitTargetTarget.value = String(S_goal)
    if (this.hasSubmitCurrentProjectedTarget) {
      this.submitCurrentProjectedTarget.value = String(projected)
    }
    if (this.hasSubmitCplTarget) this.submitCplTarget.value = "12"
  }

  validateAdvance(stepNum) {
    if (stepNum === 1) {
      const last = this.parseCount(this.hiddenLastMonthTarget?.value)
      if (last === null || last < 0) {
        return this.showError("Enter how many members you signed up last month (0 is OK).")
      }
      return true
    }
    if (stepNum === 2) {
      const a = this.parseCount(this.hiddenMtdTarget?.value)
      if (a === null || a < 0) {
        return this.showError("Enter how many you've signed up so far this month (0 is OK).")
      }
      return true
    }
    if (stepNum === 3) {
      const g = (this.growthGoalFieldTarget?.value || "").trim()
      if (!g || !this.isValidGoalKey(g)) {
        return this.showError("Choose a growth target for this month's sign-ups.")
      }
      return true
    }
    return true
  }

  validateStep4() {
    const name = this.fieldValue("name")
    const email = this.fieldValue("email")
    if (!name?.trim()) return this.showError("Add your name so we can personalize your strategy.")
    if (!email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return this.showError("Add a valid email so we can send your strategy.")
    }
    return true
  }

  fieldValue(field) {
    const el = this.element.querySelector(`[name="calc[${field}]"]`)
    return el?.value?.trim() || ""
  }

  showError(message) {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
      this.errorTarget.hidden = false
    }
    return false
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.classList.add("hidden")
      this.errorTarget.hidden = true
    }
  }

  render() {
    this.stepTargets.forEach((el) => {
      const stepNum = parseInt(el.dataset.step, 10)
      const on = stepNum === this.stepValue
      el.hidden = !on
      el.classList.toggle("hidden", !on)
    })

    if (this.hasProgressFillTarget) {
      this.progressFillTarget.style.width = `${(this.stepValue / TOTAL_STEPS) * 100}%`
    }

    if (this.hasProgressLabelTarget) {
      const labels = [
        "",
        "Sign-ups last month",
        "Sign-ups this month",
        "Growth target",
        "Your plan"
      ]
      this.progressLabelTarget.textContent = `Step ${this.stepValue} of ${TOTAL_STEPS} \u00B7 ${labels[this.stepValue] || ""}`
    }

    if (this.hasBackButtonTarget) {
      this.backButtonTarget.classList.toggle("hidden", this.stepValue === 1)
    }

    const S_last = this.parseCount(this.hiddenLastMonthTarget?.value) ?? 0
    if (this.hasFixedThirtyWrapTarget) {
      this.fixedThirtyWrapTarget.classList.toggle("hidden", S_last >= 20)
    }

    const bar = this.element.querySelector('[role="progressbar"]')
    if (bar) {
      bar.setAttribute("aria-valuenow", String(this.stepValue))
      bar.setAttribute("aria-valuemax", String(TOTAL_STEPS))
    }

    this.recalculateViz()
    if (this.stepValue === 4) {
      this.refreshStepFourPitch()
    }
  }
}
